import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Save, Shield, Trash2 } from 'lucide-react'
import AppShell from '../../components/b2b/AppShell'
import { api, APIError } from '../../api/client'
import { useB2BAuthStore } from '../../stores/useB2BAuthStore'

// Mirror b2b.workspaces.data_retention_months SQL CHECK + the Pydantic
// allowed set in app/schemas/b2b/workspace.py.
const RETENTION_OPTIONS = [
  { value: 6,  label: '6 months — strictest' },
  { value: 12, label: '12 months — recommended default' },
  { value: 24, label: '24 months' },
  { value: 36, label: '36 months — longest allowed' },
]

/**
 * Workspace settings: edit workspace name + danger zone (delete workspace).
 *
 * Delete workspace cascades through the DB — jobs, candidates, screenings,
 * subscription, payments — all gone. Only the workspace owner can do this.
 */
export default function SettingsScreen() {
  const navigate = useNavigate()
  const member = useB2BAuthStore((s) => s.member)
  const workspace = useB2BAuthStore((s) => s.workspace)
  const updateWorkspace = useB2BAuthStore((s) => s.updateWorkspace)
  const logout = useB2BAuthStore((s) => s.logout)

  const [name, setName] = useState(workspace?.name || '')
  const [retentionMonths, setRetentionMonths] = useState(
    workspace?.data_retention_months || 12
  )
  const [retentionSaving, setRetentionSaving] = useState(false)
  const [retentionSavedAt, setRetentionSavedAt] = useState(null)
  const [retentionError, setRetentionError] = useState('')
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [error, setError] = useState('')
  const [showDelete, setShowDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteText, setDeleteText] = useState('')

  useEffect(() => {
    setName(workspace?.name || '')
    setRetentionMonths(workspace?.data_retention_months || 12)
  }, [workspace?.name, workspace?.data_retention_months])

  const isOwner = member?.role === 'owner'

  const save = async () => {
    setError('')
    if (!name.trim()) {
      setError('Workspace name cannot be empty.')
      return
    }
    setSaving(true)
    try {
      const updated = await api.patch('/api/b2b/workspaces/me', { name: name.trim() })
      updateWorkspace(updated)
      setSavedAt(new Date())
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message
      setError(typeof detail === 'string' ? detail : 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  const saveRetention = async () => {
    setRetentionError('')
    setRetentionSaving(true)
    try {
      const updated = await api.patch('/api/b2b/workspaces/me', {
        data_retention_months: retentionMonths,
      })
      updateWorkspace(updated)
      setRetentionSavedAt(new Date())
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message
      setRetentionError(typeof detail === 'string' ? detail : 'Could not save retention setting.')
    } finally {
      setRetentionSaving(false)
    }
  }

  const deleteWorkspace = async () => {
    setDeleting(true)
    try {
      await api.delete('/api/b2b/workspaces/me')
      // Clear auth so the redirect goes to landing, not back into a deleted cabinet.
      logout()
      navigate('/b2b', { replace: true })
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message
      setError(typeof detail === 'string' ? detail : 'Could not delete workspace.')
      setDeleting(false)
      setShowDelete(false)
    }
  }

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-3xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-wider text-ink-3">Settings</p>
        <h1 className="mt-1 text-3xl md:text-4xl font-bold text-ink-1">Workspace</h1>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="mt-8 bg-white rounded-3xl border border-black/5 p-6">
          <p className="text-xs font-bold uppercase tracking-wider text-ink-3">General</p>
          <label className="mt-3 block">
            <span className="text-sm font-semibold text-ink-1">Workspace name</span>
            <input
              type="text"
              className="mt-2 w-full text-[15px] outline-none bg-white border border-black/10 rounded-2xl px-4 py-3 focus:border-accent transition"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={255}
              disabled={saving || !isOwner}
              placeholder="Acme Recruiting"
            />
          </label>
          {!isOwner && (
            <p className="mt-2 text-xs text-ink-3">
              Only the workspace owner can edit settings.
            </p>
          )}
          {workspace?.slug && (
            <p className="mt-3 text-xs text-ink-3">
              URL slug: <code className="bg-canvas px-1.5 py-0.5 rounded">{workspace.slug}</code> (cannot be changed)
            </p>
          )}
          <div className="mt-5 flex items-center gap-3">
            <button
              type="button"
              onClick={save}
              disabled={saving || !isOwner || !name.trim() || name === workspace?.name}
              className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-5 min-h-[44px] rounded-2xl transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:bg-canvas disabled:text-ink-3 disabled:cursor-not-allowed disabled:hover:bg-canvas"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            {savedAt && (
              <span className="text-xs text-emerald-600 font-semibold">
                Saved at {savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>

        <div className="mt-6 bg-white rounded-3xl border border-black/5 p-6">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-accent" />
            <p className="text-xs font-bold uppercase tracking-wider text-ink-3">
              Compliance & data retention
            </p>
          </div>
          <p className="mt-3 text-sm text-ink-2">
            How long candidate data (resumes, answers, screening reports) is
            kept before automated deletion. AI audit logs are retained
            independently for 18 months for regulatory purposes.
          </p>
          {retentionError && (
            <div className="mt-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
              {retentionError}
            </div>
          )}
          <label className="mt-4 block">
            <span className="text-sm font-semibold text-ink-1">Candidate data retention</span>
            <select
              className="mt-2 w-full text-[15px] outline-none bg-white border border-black/10 rounded-2xl px-4 py-3 focus:border-accent transition disabled:bg-canvas disabled:text-ink-3"
              value={retentionMonths}
              onChange={(e) => setRetentionMonths(Number(e.target.value))}
              disabled={retentionSaving || !isOwner}
            >
              {RETENTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          {!isOwner && (
            <p className="mt-2 text-xs text-ink-3">
              Only the workspace owner can change retention.
            </p>
          )}
          <p className="mt-3 text-[11px] text-ink-3">
            GDPR Article 5(1)(e) — storage limitation. EU AI Act audit log
            retention is fixed at 18 months for all workspaces.
          </p>
          <div className="mt-5 flex items-center gap-3">
            <button
              type="button"
              onClick={saveRetention}
              disabled={
                retentionSaving ||
                !isOwner ||
                retentionMonths === (workspace?.data_retention_months || 12)
              }
              className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-5 min-h-[44px] rounded-2xl transition disabled:bg-canvas disabled:text-ink-3 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {retentionSaving ? 'Saving…' : 'Save retention setting'}
            </button>
            {retentionSavedAt && (
              <span className="text-xs text-emerald-600 font-semibold">
                Saved at {retentionSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>

        {isOwner && (
          <div className="mt-6 bg-white rounded-3xl border border-red-100 p-6">
            <p className="text-xs font-bold uppercase tracking-wider text-red-600">Danger zone</p>
            <h2 className="mt-1 text-base font-bold text-ink-1">Delete this workspace</h2>
            <p className="mt-2 text-sm text-ink-2">
              Permanently removes this workspace, all jobs, all candidates, all screenings, and your subscription.
              This cannot be undone.
            </p>
            <button
              type="button"
              onClick={() => setShowDelete(true)}
              className="mt-4 inline-flex items-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 font-bold px-4 py-2 rounded-xl transition"
            >
              <Trash2 className="w-4 h-4" />
              Delete workspace
            </button>
          </div>
        )}

        {showDelete && (
          <div
            className="fixed inset-0 z-40 flex items-center justify-center px-4"
            style={{ background: 'rgba(15,23,42,0.55)' }}
          >
            <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-ink-1">Delete workspace?</h3>
                  <p className="mt-1 text-sm text-ink-2">
                    This permanently removes <strong>{workspace?.name}</strong> and everything in it. To confirm, type
                    {' '}<strong>delete</strong>{' '}below.
                  </p>
                </div>
              </div>
              <input
                type="text"
                placeholder="delete"
                className="mt-4 w-full text-[14px] outline-none bg-white border border-black/10 rounded-xl px-4 py-2.5 focus:border-red-400 transition"
                value={deleteText}
                onChange={(e) => setDeleteText(e.target.value)}
                disabled={deleting}
              />
              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setShowDelete(false); setDeleteText('') }}
                  disabled={deleting}
                  className="px-4 py-2 rounded-xl text-sm font-semibold text-ink-2 hover:bg-canvas"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={deleteWorkspace}
                  disabled={deleting || deleteText !== 'delete'}
                  className="px-4 py-2 rounded-xl text-sm font-bold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                >
                  {deleting ? 'Deleting…' : 'Permanently delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}
